import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createSlots as _createSlots } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h3", null, "Add from text", -1)
const _hoisted_2 = { class: "max-h-[calc(100vh-300px)] overflow-auto" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "w-full" }, "Bulk Add Chapter", -1)
const _hoisted_4 = { class: "space-y-2 mb-4 max-h-[calc(100vh-20rem)] overflow-auto" }
const _hoisted_5 = { class: "flex justify-end gap-2" }
const _hoisted_6 = { class: "space-y-2 sm:space-y-4" }
const _hoisted_7 = { class: "flex items-center gap-1 sm:gap-2" }
const _hoisted_8 = { class: "flex items-center justify-end gap-2" }

import { Button, Divider, Input, Modal, Textarea } from 'ant-design-vue';
import { HttpStatusCode } from 'axios';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
	CopyFilled,
	DeleteFilled,
	FileAddOutlined,
	LoadingOutlined,
	PlusOutlined
} from '@ant-design/icons-vue';
import BulkDownloadProgress from '@/components/disposable/BulkDownloadProgress.vue';
import { Map } from '@/model/universal';
import { createComicChapter } from '@/repository/comic';
import { scrapingComic, scrapingNovel } from '@/util/scraping';
import { ComicType } from '@/model/comic';

type InputChapter = {
	chapter: number;
	links: string[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ComicBulkAddChapter',
  props: {
    isOpened: { type: Boolean },
    onClose: { type: Function },
    comicUUID: {},
    comicType: {}
  },
  emits: ["onBeforeClose"],
  setup(__props: any, { emit }) {

const props = __props;





const store = useStore();

const isOpenModal = ref(false);
const isAdding = ref(false);
const count = ref(1);
const inputs = ref<InputChapter[]>([{ chapter: 0, links: [''] }]);
const statuses = ref<('Success' | 'Warning' | 'Error' | undefined)[]>([undefined]);
const timestamp = ref(new Date().getTime());

const successTotal = computed(() => statuses.value.filter((status) => status === 'Success').length);
const errorTotal = computed(() => statuses.value.filter((status) => status === 'Error').length);
const warningTotal = computed(() => statuses.value.filter((status) => status === 'Warning').length);
const isInvalid = computed(
	() =>
		inputs.value.filter(
			(input) => input.links.length === 0 || input.links.filter((link) => !link).length > 0
		).length > 0
);
const isChapterDuplicate = computed(
	() =>
		Array.from(new Set(inputs.value.map((input) => input.chapter))).length !== inputs.value.length
);

const text = ref('');
async function addFromText(mode: 'ADD' | 'REPLACE') {
	const texts = text.value
		.split('\n')
		.filter((value) => value.trim().length > 0)
		.map((value) => value.trim());

	if (texts.length < 1) {
		return;
	}

	const append = (): InputChapter[] => {
		const values: Map<string[]> = {};
		for (let text of texts) {
			let chapter = 0;
			if (text.indexOf(':') !== text.length - 1 && text.length > 0) {
				const chapterText = text.substring(0, text.indexOf(':'));
				if (Number(chapterText) >= 0) {
					text = text.substring(text.indexOf(':') + 1);
					chapter = Number(chapterText);
				}
			}
			const txts = text.split('/').filter((txt) => txt);
			chapter = chapter || Number(txts[txts.length - 1]) || 0;
			if (!values[chapter.toString()]) {
				values[chapter.toString()] = [];
			}
			values[chapter.toString()].push(text);
		}
		return Object.keys(values).map((chapter) => ({
			chapter: Number(chapter),
			links: values[chapter]
		}));
	};

	if (mode === 'REPLACE') {
		inputs.value = [...append()];
	} else {
		inputs.value = [...inputs.value, ...append()];
	}
	count.value = inputs.value.length || 1;
	text.value = '';
	isOpenModal.value = false;
}

function addItem(inputIndex?: number) {
	if (inputIndex === undefined) {
		inputs.value.push({ chapter: 0, links: [''] });
		count.value++;
	} else {
		inputs.value[inputIndex].links.push('');
	}
}

function removeItem(inputIndex: number, linkIndex?: number) {
	if (count.value > 1 && linkIndex === undefined) {
		inputs.value = inputs.value.filter((_, i) => i !== inputIndex);
		count.value--;
	} else if (inputs.value[inputIndex].links.length > 1 && linkIndex !== undefined) {
		inputs.value[inputIndex].links = inputs.value[inputIndex].links.filter(
			(_, i) => i !== linkIndex
		);
	}
}

async function pasteItem(inputIndex: number, linkIndex: number) {
	inputs.value[inputIndex].links[linkIndex] = await navigator.clipboard.readText();
}

function clear() {
	if (errorTotal.value > 0 && inputs.value.length === statuses.value.length) {
		inputs.value = inputs.value.filter((_, index) => statuses.value[index] === 'Error');
		statuses.value = [undefined];
		count.value = inputs.value.length;
	}
}

async function submit() {
	if (!props.isOpened || isAdding.value || isInvalid.value || isChapterDuplicate.value) {
		return;
	}
	const currentTimestamp = timestamp.value;
	isAdding.value = true;
	statuses.value = inputs.value.map(() => undefined);
	try {
		for (let i = 0; i < inputs.value.length; i++) {
			const input = inputs.value[i];
			const files: File[] = [];
			let content = '';
			switch (props.comicType) {
				case ComicType.Novel: {
					for (const link of input.links) {
						try {
							const result = await scrapingNovel(link);
							content += result.content;
							files.push(...result.files);
						} catch (error) {
							statuses.value[i] = 'Error';
							break;
						}
					}
					if (!content || statuses.value[i] === 'Error') {
						statuses.value[i] = 'Error';
						continue;
					}
					break;
				}

				// Scraping Manga and Manhwa
				default: {
					const images: Map<File> = {};
					for (const link of input.links) {
						const length = Object.keys(images).length;
						let retryCount = 10;
						let round = 0;
						while (round < retryCount) {
							try {
								await scrapingComic(link, (file, index) => {
									if (timestamp.value === currentTimestamp && props.isOpened) {
										images[length + index] = file;
									}
								});
								break;
							} catch (error) {
								round++;
							}
						}
						if (round === retryCount) {
							statuses.value[i] = 'Error';
							break;
						}
					}

					if (Object.keys(images).length === 0 || statuses.value[i] === 'Error') {
						statuses.value[i] = 'Error';
						continue;
					}

					// Creating
					const sortIndex = Object.keys(images).sort((a, b) => Number(a) - Number(b));
					for (let index of sortIndex) {
						files.push(images[index]);
					}
					break;
				}
			}

			try {
				const res = await createComicChapter(
					props.comicUUID,
					input.chapter.toString(),
					input.links[0],
					files,
					content
				);
				statuses.value[i] =
					res.status === HttpStatusCode.Ok
						? 'Success'
						: res.status === HttpStatusCode.Conflict
							? 'Warning'
							: 'Error';
			} catch (error) {
				statuses.value[i] = 'Error';
			}
		}
		await emit('onBeforeClose');
		if (statuses.value.filter((status) => status !== 'Success').length > 0) {
			throw Error('Some chapter has been error!');
		}
		store.dispatch('alert', { type: 'success', message: `Bulk add chapters successfully!` });
		close();
	} catch (error) {
		store.dispatch('alert', {
			type: 'error',
			message: `${error}` || 'something went wrong, please try again!'
		});
	} finally {
		isAdding.value = false;
	}
}

function initialize() {
	isAdding.value = false;
	count.value = 1;
	inputs.value = [{ chapter: 0, links: [''] }];
	statuses.value = [undefined];
}

function close() {
	initialize();
	props.onClose();
}

watch(
	() => props.isOpened,
	(isOpened) => isOpened && initialize()
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Modal), {
      open: isOpenModal.value,
      onCancel: _cache[4] || (_cache[4] = () => (isOpenModal.value = false))
    }, {
      title: _withCtx(() => [
        _hoisted_1
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(Button), {
          type: "primary",
          danger: "",
          onClick: _cache[1] || (_cache[1] = () => (isOpenModal.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Cancel ")
          ]),
          _: 1
        }),
        _createVNode(_unref(Button), {
          type: "primary",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (addFromText('REPLACE'))),
          disabled: text.value.trim().length === 0 || isAdding.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Replace ")
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(Button), {
          type: "primary",
          class: "bg-green-500 hover:!bg-green-400 [&[disabled]]:!bg-gray-50",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (addFromText('ADD'))),
          disabled: text.value.trim().length === 0 || isAdding.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Add ")
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(Textarea), {
            placeholder: "Input urls separate by new line",
            autoSize: "",
            allowClear: "",
            value: text.value,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (text.value = $event.target.value || ''))
          }, null, 8, ["value"])
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_unref(Modal), {
      open: _ctx.isOpened,
      closable: false
    }, {
      title: _withCtx(() => [
        _hoisted_3,
        _createVNode(_unref(Divider), { class: "p-0 my-4" })
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(Divider), {
          class: _normalizeClass('p-0 ' + (isAdding.value ? 'mt-4 mb-0' : 'my-4'))
        }, null, 8, ["class"]),
        (isAdding.value)
          ? (_openBlock(), _createBlock(BulkDownloadProgress, {
              key: 0,
              total: count.value,
              success: successTotal.value,
              failed: errorTotal.value + warningTotal.value
            }, null, 8, ["total", "success", "failed"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(Button), {
            type: "primary",
            class: "bg-yellow-500 hover:!bg-yellow-600 disabled:!bg-gray-50",
            onClick: clear,
            disabled: isAdding.value || errorTotal.value === 0 || warningTotal.value + successTotal.value === 0
          }, {
            default: _withCtx(() => [
              _createTextVNode("Clear")
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_unref(Button), {
            key: "back",
            onClick: close,
            disabled: isAdding.value
          }, {
            default: _withCtx(() => [
              _createTextVNode("Cancel")
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_unref(Button), {
            key: "submit",
            type: "primary",
            disabled: isAdding.value || isInvalid.value || isChapterDuplicate.value,
            onClick: submit
          }, _createSlots({
            default: _withCtx(() => [
              _createTextVNode(" OK ")
            ]),
            _: 2
          }, [
            (isAdding.value)
              ? {
                  name: "icon",
                  fn: _withCtx(() => [
                    _createVNode(_unref(LoadingOutlined))
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["disabled"])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(Button), {
              class: _normalizeClass(
						'bg-yellow-500 border-none ' +
						(isAdding.value ? '!bg-gray-200 border-none' : 'hover:bg-yellow-400 hover:!text-black')
					),
              disabled: isAdding.value,
              onClick: _cache[5] || (_cache[5] = () => (isOpenModal.value = true))
            }, {
              icon: _withCtx(() => [
                _createVNode(_unref(FileAddOutlined))
              ]),
              default: _withCtx(() => [
                _createTextVNode(" Add from text ")
              ]),
              _: 1
            }, 8, ["class", "disabled"]),
            _createVNode(_unref(Button), {
              onClick: _cache[6] || (_cache[6] = () => addItem()),
              type: "primary",
              disabled: isAdding.value,
              class: _normalizeClass(isAdding.value && '!bg-gray-200 border-none')
            }, {
              icon: _withCtx(() => [
                _createVNode(_unref(PlusOutlined))
              ]),
              default: _withCtx(() => [
                _createTextVNode(" Add Chapter ")
              ]),
              _: 1
            }, 8, ["disabled", "class"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Array(count.value), (_, index) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "space-y-1 sm:space-y-2",
                key: index
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_unref(Input), {
                    type: "number",
                    value: inputs.value[index].chapter,
                    onChange: ($event: any) => (inputs.value[index].chapter = +$event.target.value!),
                    class: _normalizeClass(
								'text-start text-full ' +
								(statuses.value[index] === 'Success'
									? '!bg-green-500 !text-white'
									: statuses.value[index] === 'Warning'
										? '!bg-yellow-500 !text-black'
										: statuses.value[index] === 'Error'
											? '!bg-red-500 !text-white'
											: '!bg-white')
							),
                    placeholder: "Chapter",
                    disabled: isAdding.value,
                    min: 0,
                    max: 999
                  }, null, 8, ["value", "onChange", "class", "disabled"]),
                  _createVNode(_unref(Button), {
                    class: _normalizeClass(
								'bg-green-400 text-white border-none hover:!bg-green-500 hover:!text-gray-800' +
								(isAdding.value && ' !bg-gray-200 border-none')
							),
                    onClick: ($event: any) => (addItem(index)),
                    disabled: isAdding.value
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(_unref(PlusOutlined))
                    ]),
                    _: 2
                  }, 1032, ["class", "onClick", "disabled"]),
                  (index > 0 || (index === 0 && count.value > 1))
                    ? (_openBlock(), _createBlock(_unref(Button), {
                        key: 0,
                        class: _normalizeClass(
								'bg-red-600 text-white border-none hover:bg-red-700 hover:!text-gray-300' +
								(isAdding.value && ' !bg-gray-200 border-none')
							),
                        onClick: ($event: any) => (removeItem(index)),
                        disabled: isAdding.value
                      }, {
                        icon: _withCtx(() => [
                          _createVNode(_unref(DeleteFilled))
                        ]),
                        _: 2
                      }, 1032, ["class", "onClick", "disabled"]))
                    : _createCommentVNode("", true)
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inputs.value[index].links, (_, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "ml-2 sm:ml-4 flex items-center gap-1 sm:gap-2",
                    key: idx
                  }, [
                    _createVNode(_unref(Input), {
                      value: inputs.value[index].links[idx],
                      "onUpdate:value": ($event: any) => ((inputs.value[index].links[idx]) = $event),
                      class: "text-start text-full !bg-white",
                      placeholder: "Link",
                      disabled: isAdding.value
                    }, null, 8, ["value", "onUpdate:value", "disabled"]),
                    _createVNode(_unref(Button), {
                      onClick: ($event: any) => (pasteItem(index, idx)),
                      disabled: isAdding.value,
                      class: _normalizeClass((isAdding.value && '!bg-gray-200 border-none ') + '')
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(_unref(CopyFilled))
                      ]),
                      _: 2
                    }, 1032, ["onClick", "disabled", "class"]),
                    (idx > 0 || (idx === 0 && inputs.value[index].links.length > 1))
                      ? (_openBlock(), _createBlock(_unref(Button), {
                          key: 0,
                          class: _normalizeClass(
								'bg-red-600 text-white border-none hover:bg-red-700 hover:!text-gray-300' +
								(isAdding.value && ' !bg-gray-200 border-none')
							),
                          onClick: ($event: any) => (removeItem(index, idx)),
                          disabled: isAdding.value
                        }, {
                          icon: _withCtx(() => [
                            _createVNode(_unref(DeleteFilled))
                          ]),
                          _: 2
                        }, 1032, ["class", "onClick", "disabled"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ]))
            }), 128))
          ])
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})